import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  sentryLog,
  getErrorMessage,
  getErrorStatus,
} from '@/utils/sentryLogger';
import { LocalStorage, KEYS } from '@/utils/LocalStorage';
import { useUserDetails } from '@/hooks/useUserDetails';
import { useHasRecommendation } from '@/hooks/useHasRecommendation';
import TokenManager from '@viome/viome-base/src/api/TokenManager';
import {
  useSMPSessionInit,
  useGetSubscriptions,
  useGetSubscriptionDetail,
  useGetPlatformState,
  useGetCustomerDiscounts,
  useGetUIMessages,
  useGetChangePlanData,
  useCanSkipOrder,
  useGetSubscriptionState,
} from '@viome/viome-base/src/usecase/SMP/SMPQuery';
import type {
  SMPSubscription,
  SMPQueryParams,
  SMPPlanComponent,
  DiscountsResponse,
} from '@viome/viome-base/src/usecase/SMP/SMP.model';
import {
  isSubscriptionCancelled,
  isKitSubscription,
  isSubscriptionActiveState,
} from '../utils/smpHelpers';
import { useIframeModeStore } from '@/hooks/useIframeModeStore';
import { getAccessContext } from '@/utils/accessChannel';
import { Sentry } from '@/main.tsx';

/** Hook to get supplement eligibility flags from bootup data */
const useBootupFlags = () => {
  const localBootup = LocalStorage.getItem(KEYS.BOOTUP);
  if (!localBootup)
    return {
      hasPersonalizedSupplementRecommendations: false,
      isEligibleForSupplements: false,
    };

  try {
    const parsed = JSON.parse(localBootup);
    return {
      hasPersonalizedSupplementRecommendations:
        parsed.hasPersonalizedSupplementRecommendationsExists ?? false,
      isEligibleForSupplements: parsed.isEligibleForSupplements ?? false,
    };
  } catch {
    return {
      hasPersonalizedSupplementRecommendations: false,
      isEligibleForSupplements: false,
    };
  }
};

interface UseSMPDataOptions {
  skip?: {
    discounts?: boolean;
    changePlan?: boolean;
    uiMessages?: boolean;
    canSkip?: boolean;
    holdingPattern?: boolean;
  };
  /**
   * When set, the page's "something went wrong" error is logged to Sentry
   * tagged with this screen name. Omit to disable logging (default).
   */
  screenName?: string;
}

/** Main hook to provide all SMP page data */
export const useSMPData = (options?: UseSMPDataOptions) => {
  const {
    discounts: skipDiscounts,
    changePlan: skipChangePlan,
    uiMessages: skipUIMessages,
    canSkip: skipCanSkip,
    holdingPattern: skipHoldingPattern,
  } = options?.skip ?? {};
  const screenName = options?.screenName;
  const userDetails = useUserDetails();
  const iframeModeState = useIframeModeStore();
  const customerId = String(
    userDetails.shopifyUserID || iframeModeState.customerId || '',
  );
  const [searchParams] = useSearchParams();
  // URL query first; fall back to the iframe-mode store (mobile webview passes
  // subscriptionId as an in_iframe param that may not survive client-side nav).
  const subscriptionIdFromQuery =
    Number(searchParams.get('subscriptionId')) ||
    iframeModeState.subscriptionId ||
    null;
  const bootupHasRecommendation = useHasRecommendation();
  const hasRecommendation =
    bootupHasRecommendation || iframeModeState.hasRecommendation;
  const bootupFlags = useBootupFlags();
  const hasPersonalizedSupplementRecommendations =
    bootupFlags.hasPersonalizedSupplementRecommendations ||
    iframeModeState.hasPersonalizedSupplementRecommendations;
  const isEligibleForSupplements =
    bootupFlags.isEligibleForSupplements ||
    iframeModeState.isEligibleForSupplements;

  // Step 1: Initialize SMP session by calling /app/loginByToken
  // This establishes cookies on the SMP domain (test.stage.azure.viome.com)
  // All subsequent SMP API calls depend on this succeeding first.
  const authToken = TokenManager.getAuthToken();
  const {
    data: smpSessionUser,
    isLoading: isLoadingSession,
    isError: isErrorSession,
    error: sessionError,
  } = useSMPSessionInit(authToken);
  const isSessionReady = !!smpSessionUser;
 const smpUserId = smpSessionUser?.userID;
  // loginByToken is the only auth path guaranteed for every SMP access
  // channel (native-app webview included, which never populates the app's
  // normal localStorage user record) — set Sentry's user from its response
  // so SMP page errors are attributable regardless of channel.
  useEffect(() => {
    if (smpUserId) {
      Sentry.setUser({
        id: smpUserId,
        userName: smpSessionUser.fullName,
      });
    }
  }, [smpUserId, smpSessionUser?.fullName]);

  // Step 2: Fetch all subscriptions (only after session is established)
  const {
    data: subscriptionsData,
    isLoading: isLoadingSubscriptions,
    isError: isErrorSubscriptions,
    error: subscriptionsError,
    refetch: refetchSubscriptions,
  } = useGetSubscriptions(customerId, {
    enabled: !!isSessionReady && !!customerId,
  });

  const subscriptions = subscriptionsData?.subscriptions;
  const platform = subscriptionsData?.platform ?? '';

  // Active-like subscriptions (active / paused / skipped) — the ones a customer
  // can actually manage, matching SMP's isSubscriptionActiveState elsewhere.
  // Historical cancelled/inactive subs stay in the list but shouldn't drive
  // selection.
  const activeSubscriptions = useMemo(
    () =>
      (subscriptions ?? []).filter((s) =>
        isSubscriptionActiveState(s.subscription_status),
      ),
    [subscriptions],
  );

  // Determine the active subscription. The URL `?subscriptionId=` is the
  // canonical selection (set by the account selector):
  // - explicit id present → the matching subscription (undefined = invalid id)
  // - no id + single subscription → that one (selector bypass)
  // - no id + multiple subscriptions → derive from the endpoint: the most
  //   recently updated active/paused sub (falls back to most recent overall).
  //   Handles the live case where one supplement sub sits alongside old
  //   cancelled subs and no id is passed.
  const activeSubscription: SMPSubscription | undefined = useMemo(() => {
    if (!subscriptions || subscriptions.length === 0) return undefined;

    if (subscriptionIdFromQuery) {
      return subscriptions.find((s) => s.id === subscriptionIdFromQuery);
    }

    if (subscriptions.length === 1) return subscriptions[0];

    const pool =
      activeSubscriptions.length > 0 ? activeSubscriptions : subscriptions;
    // Unparseable dates sort to the bottom (epoch 0) so they never win [0].
    const toTime = (d: string) => {
      const t = new Date(d).getTime();
      return Number.isNaN(t) ? 0 : t;
    };
    return [...pool].sort(
      (a, b) => toTime(b.updated_at) - toTime(a.updated_at),
    )[0];
  }, [subscriptions, subscriptionIdFromQuery, activeSubscriptions]);

  const subscriptionId = activeSubscription?.id ?? 0;

  const queryParams: SMPQueryParams = useMemo(
    () => ({
      subscriptionId,
      customerId,
    }),
    [subscriptionId, customerId],
  );

  // Step 3: Fetch detailed subscription data (only after session + subscription resolved)
  const {
    data: subscriptionDetail,
    isLoading: isLoadingDetail,
    isError: isErrorDetail,
    error: detailError,
  } = useGetSubscriptionDetail(queryParams, {
    enabled: !!isSessionReady && !!subscriptionId && !!customerId,
  });

  // Step 4: Fetch platform state (only after session is established)
  const {
    data: platformState,
    isLoading: isLoadingPlatformState,
    isError: isErrorPlatformState,
  } = useGetPlatformState(subscriptionId, {
    enabled: !!isSessionReady && !!subscriptionId,
  });

  const isCancelled = activeSubscription
    ? isSubscriptionCancelled(activeSubscription.subscription_status)
    : false;
  const subscriptionsResolved = !!subscriptions && subscriptions.length > 0;
  // Detail is the reliable line_items source (the list may not carry them).
  const isKit = isKitSubscription(subscriptionDetail?.line_items);

  // An explicit `?subscriptionId=` that isn't in the (loaded) list.
  const isSubscriptionIdUnmatched =
    !!subscriptionIdFromQuery && subscriptionsResolved && !activeSubscription;

  // A just-added subscription can be absent from an already-cached list, which
  // would bounce the user straight back to the account page. Refetch once per
  // id before calling it invalid.
  const [isRefetchingSubscriptions, setIsRefetchingSubscriptions] =
    useState(false);
  const refetchedForIdRef = useRef<number | null>(null);

  useEffect(() => {
    if (!isSubscriptionIdUnmatched) return;
    if (refetchedForIdRef.current === subscriptionIdFromQuery) return;

    refetchedForIdRef.current = subscriptionIdFromQuery;
    setIsRefetchingSubscriptions(true);
    refetchSubscriptions().finally(() => setIsRefetchingSubscriptions(false));
  }, [
    isSubscriptionIdUnmatched,
    subscriptionIdFromQuery,
    refetchSubscriptions,
  ]);

  const isInvalidSubscription =
    isSubscriptionIdUnmatched &&
    refetchedForIdRef.current === subscriptionIdFromQuery &&
    !isRefetchingSubscriptions;

  // Step 5: Fetch customer discounts (skip for cancelled subscriptions — the API errors out)
  // Wait for subscriptions to resolve first so we know the cancellation status before firing.
  const {
    data: discountsRaw,
    isLoading: isLoadingDiscounts,
    isError: isErrorDiscounts,
  } = useGetCustomerDiscounts(subscriptionId, {
    enabled:
      !skipDiscounts &&
      !!isSessionReady &&
      !!subscriptionId &&
      subscriptionsResolved &&
      !isCancelled,
  });

  // Step 6: Fetch change plan data to get individual product components.
  // The subscription detail API returns combined line items (e.g. "Full Body Test w/ Biotics"),
  // but the change plan API returns individual components (test kit, supplements, biotics, etc.).
  const {
    data: changePlanData,
    isLoading: isLoadingChangePlan,
    isError: isErrorChangePlan,
  } = useGetChangePlanData(subscriptionId, {
    enabled:
      !skipChangePlan &&
      !!isSessionReady &&
      !!subscriptionId &&
      !!subscriptionDetail,
  });

  // Step 7: Fetch UI messages for subscription management
  const {
    data: uiMessages,
    isLoading: isLoadingMessages,
    isError: isErrorMessages,
  } = useGetUIMessages('subscription_management', {
    enabled: !skipUIMessages && !!isSessionReady,
  });

  // Step 8: Fetch can_skip status (only when skip order link is visible)
  // Skip Next Order applies to supplement subs (recs) and kit subs.
  const showSkipOrder =
    (isKit ||
      (isEligibleForSupplements && hasPersonalizedSupplementRecommendations)) &&
    !isCancelled;
  const { data: canSkipData } = useCanSkipOrder(subscriptionId, {
    enabled:
      !skipCanSkip && !!isSessionReady && !!subscriptionId && showSkipOrder,
  });

  // Step 9: Fetch holding pattern state
  const { data: holdingPatternData } = useGetSubscriptionState(
    'HOLDING_PATTERN',
    subscriptionId,
    { enabled: !skipHoldingPattern && !!isSessionReady && !!subscriptionId },
  );

  const canSkip = showSkipOrder ? (canSkipData?.can_skip ?? true) : true;
  const isOnHoldingPatternState = useMemo(() => {
    return (
      Array.isArray(holdingPatternData?.openStates) &&
      holdingPatternData.openStates.length > 0
    );
  }, [holdingPatternData]);

  const { customerDiscounts, maxAllowedDiscounts } = useMemo((): {
    customerDiscounts: DiscountsResponse[];
    maxAllowedDiscounts: number;
  } => {
    if (!discountsRaw) return { customerDiscounts: [], maxAllowedDiscounts: 1 };
    const raw = discountsRaw as unknown;

    if (Array.isArray(raw)) {
      return {
        customerDiscounts: raw.filter(
          (d: DiscountsResponse) => !!d?.discountCodeName,
        ),
        maxAllowedDiscounts: 1,
      };
    }

    const obj = raw as Record<string, unknown>;
    const nested = obj?.data;

    if (Array.isArray(nested)) {
      return {
        customerDiscounts: nested.filter(
          (d: DiscountsResponse) => !!d?.discountCodeName,
        ),
        maxAllowedDiscounts: 1,
      };
    }

    const nestedObj = nested as
      | ({
          discountCodes?: unknown;
          maxAllowedDiscounts?: number;
        } & DiscountsResponse)
      | undefined;
    if (Array.isArray(nestedObj?.discountCodes)) {
      return {
        customerDiscounts: (
          nestedObj.discountCodes as DiscountsResponse[]
        ).filter((d) => !!d?.discountCodeName),
        maxAllowedDiscounts: nestedObj.maxAllowedDiscounts ?? 1,
      };
    }
    if (nestedObj?.discountCodeName)
      return { customerDiscounts: [nestedObj], maxAllowedDiscounts: 1 };
    const flat = raw as DiscountsResponse;
    if (flat?.discountCodeName)
      return { customerDiscounts: [flat], maxAllowedDiscounts: 1 };

    return { customerDiscounts: [], maxAllowedDiscounts: 1 };
  }, [discountsRaw]);

  const customerDiscount =
    customerDiscounts.length > 0 ? customerDiscounts[0] : null;

  const isLoading =
    isLoadingSession ||
    isLoadingSubscriptions ||
    isRefetchingSubscriptions ||
    isLoadingDetail ||
    isLoadingPlatformState ||
    (!skipDiscounts && !isCancelled && isLoadingDiscounts) ||
    (!skipChangePlan && isLoadingChangePlan) ||
    (!skipUIMessages && isLoadingMessages);
  const isError = isErrorSession || isErrorSubscriptions || isErrorDetail;
  const hasNonCriticalError =
    isErrorPlatformState ||
    isErrorDiscounts ||
    isErrorChangePlan ||
    isErrorMessages;
  const showMaintenance = platformState?.data?.show_maintenance ?? false;

  // Log the page's "something went wrong" error to Sentry — the same condition
  // SMPLandingPage uses to render <ErrorLoadingPage errorType="error">
  // (isError || !subscriptionDetail), gated on !isLoading so we skip the
  // transient loading state. Only fires when a screenName is supplied (opt-in),
  // and once per error occurrence rather than every render.
  // Dedup key of the last logged failure, not a boolean: if the failure
  // transitions while the page stays errored (e.g. none → getSubscriptions),
  // the new state re-logs instead of being suppressed. Reset when recovered.
  const loggedErrorKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!screenName) return;
    // Mirror SMPLandingPage's render precedence: an invalid `?subscriptionId=`
    // redirects to the account selector (Navigate) BEFORE the error page, so it
    // isn't a "page error" — don't log it as one.
    const showPageError =
      !isLoading && !isInvalidSubscription && (isError || !subscriptionDetail);
    if (!showPageError) {
      loggedErrorKeyRef.current = null;
      return;
    }

    // Identify which API failed (call order: session → subscriptions → detail).
    // No error flag but still on the error page = subscription never resolved
    // (empty list / unmatched id) rather than an API failure.
    const failed = isErrorSession
      ? { api: 'loginByToken', error: sessionError }
      : isErrorSubscriptions
        ? { api: 'getSubscriptions', error: subscriptionsError }
        : isErrorDetail
          ? { api: 'getSubscriptionDetail', error: detailError }
          : { api: 'none', error: undefined };

    // When no API errored but the page still can't render, pinpoint why the
    // subscription never resolved rather than logging one vague catch-all.
    const noApiErrorReason = (): string => {
      if (!subscriptionsData) return 'getSubscriptions returned no data';
      if (!Array.isArray(subscriptions))
        return 'getSubscriptions response missing `subscriptions` array (unexpected BE shape)';
      if (subscriptions.length === 0)
        return 'Customer has no subscriptions (empty list)';
      if (
        subscriptionIdFromQuery &&
        !subscriptions.some((s) => s.id === subscriptionIdFromQuery)
      )
        return `subscriptionId "${subscriptionIdFromQuery}" not found among ${subscriptions.length} subscription(s)`;
      if (!subscriptionId)
        return `Could not resolve an active subscription from ${subscriptions.length} subscription(s)`;
      return `getSubscriptionDetail returned no data for subscriptionId "${subscriptionId}" (no error thrown)`;
    };

    const errorMessage =
      failed.api === 'none'
        ? noApiErrorReason()
        : getErrorMessage(failed.error);
    const key = `${failed.api}|${errorMessage}`;
    if (loggedErrorKeyRef.current === key) return;
    loggedErrorKeyRef.current = key;

    sentryLog('error', 'SMP page error', {
      screen: screenName,
      subscriptionId,
      ...getAccessContext(),
      subscriptionIdFromQuery: subscriptionIdFromQuery ?? '(none)',
      subscriptionCount: subscriptions?.length ?? 0,
      hasSubscriptionDetail: !!subscriptionDetail,
      failedApi: failed.api === 'none' ? 'No API Failure' : failed.api,
      errorMessage,
      errorStatus: getErrorStatus(failed.error),
    });
  }, [
    screenName,
    isLoading,
    isError,
    isInvalidSubscription,
    subscriptionDetail,
    subscriptionId,
    subscriptionIdFromQuery,
    subscriptions,
    subscriptionsData,
    isErrorSession,
    isErrorSubscriptions,
    isErrorDetail,
    sessionError,
    subscriptionsError,
    detailError,
  ]);

  // Determine if there's an order in progress from the lacore_order data
  const hasOrderInProgress = useMemo(() => {
    if (!subscriptionDetail?.lacore_order) return false;
    const { current_state } = subscriptionDetail.lacore_order;
    return (
      current_state === 'Placed' ||
      current_state === 'Processing' ||
      current_state === 'Shipped'
    );
  }, [subscriptionDetail]);

  const productComponents: SMPPlanComponent[] =
    changePlanData?.current_components ?? [];

  return {
    customerId,
    subscriptionId,
    subscriptionDetail,
    subscriptions,
    platform,
    productComponents,
    customerDiscount,
    customerDiscounts,
    maxAllowedDiscounts,
    hasRecommendation,
    hasPersonalizedSupplementRecommendations,
    isEligibleForSupplements,
    hasOrderInProgress,
    canSkip,
    isOnHoldingPatternState,
    showMaintenance,
    uiMessages,
    isLoading,
    isError,
    hasNonCriticalError,
    isInvalidSubscription,
    isSessionReady: !!isSessionReady,
    queryParams,
  };
};
